import type { ChatComment } from '@getaccept/lib-shared-new/src/chat/types/chat-comment';
import { SigningMergeTagHelper } from '@getaccept/lib-shared-new/src/helpers/signing-merge-tag.helper';
import type { Entity } from '@getaccept/lib-shared-new/src/entity/signing-site/types/entity';
import type { SigningPageDocument } from '@getaccept/lib-shared-new/src/types/signing-page-document';
import type { SigningPageRecipient } from '@getaccept/lib-shared-new/src/types/signing-page-recipient';
import type { Ref } from 'vue';
import { ref } from 'vue';
import { defineStore } from 'pinia';
import { EmojiHelper } from '@getaccept/lib-shared-new/src/emoji/helpers/emoji.helper';
import { DateTime } from 'luxon';
import { useStorage } from '@vueuse/core';
import { LocalStorageSubKey } from '../../local-storage/type/subkey';
import { ChatService } from '../../api/chat/services/chat.service';
import { LocalStorageHelper } from '../../local-storage/helpers/local-storage.helper';

export const useChatStore = defineStore('chat', () => {
  const isLoading: Ref<boolean> = ref(false);
  const comments: Ref<ChatComment[]> = ref([]);
  const loadChat = async (
    entity: Entity,
    document: SigningPageDocument,
    currentRecipient: SigningPageRecipient
  ) => {
    isLoading.value = true;
    try {
      const comments: ChatComment[] = await ChatService.getComments();
      const parsedComments: ChatComment[] = comments.map((comment: ChatComment) => ({
        ...comment,
        commentText: SigningMergeTagHelper.replaceTags(comment.commentText, {
          recipient: currentRecipient,
          document,
          entity,
        }),
      }));
      loadChatSuccess(parsedComments);
    } catch (error) {
      console.error('Could not load chat messages');
    } finally {
      isLoading.value = false;
    }
  };

  const commentsRead: Ref<ChatComment[]> = ref(null);
  const loadChatSuccess = (comments: ChatComment[]) => {
    if (!commentsRead.value) {
      commentsRead.value = comments;
    }
    setChatComments(comments);
    isLoading.value = false;
  };

  const addComment = async (
    commentText: string,
    recipientId: string,
    authId: string,
    documentId: string
  ) => {
    const comment: ChatComment = {
      recipientId,
      commentText,
      createdAt: DateTime.utc().toISO(),
    };
    const staleComments = [...comments.value];
    setChatComments([...comments.value, comment]);
    try {
      const success: boolean = await ChatService.addComment(
        documentId,
        recipientId,
        authId,
        commentText
      );
      if (!success) {
        setChatComments(staleComments);
      }
    } catch (e) {
      setChatComments(staleComments);
    }
  };

  const notificationDotDelay = 2500;

  const readComments = () => {
    setTimeout(() => {
      commentsRead.value = comments.value;
    }, notificationDotDelay);
  };

  const isOpen: Ref<boolean> = useStorage(
    LocalStorageHelper.buildKey(window.location.href, LocalStorageSubKey.ChatOpen),
    false
  );
  const setIsOpen = (doOpen: boolean) => {
    if (isOpen.value) {
      readComments();
    }
    isOpen.value = doOpen;
  };

  const systemMessageOpen: Ref<boolean> = ref(false);
  const setSystemMessageOpen = (isOpen: boolean) => {
    systemMessageOpen.value = isOpen;
  };

  const setChatComments = (commentsToSet: ChatComment[]) => {
    const parsedComments: ChatComment[] = [];
    commentsToSet.forEach((comment: ChatComment) => {
      const parsedCommentText: string = EmojiHelper.decode(comment.commentText);
      parsedComments.push({ ...comment, commentText: parsedCommentText });
    });
    comments.value = parsedComments;
  };

  return {
    isLoading,
    comments,
    isOpen,
    commentsRead,
    systemMessageOpen,

    loadChat,
    addComment,
    setIsOpen,
    setSystemMessageOpen,
    readComments,
  };
});
